import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import type { MitLimits, MitLimitsRequest } from '../../core/models';

export type MitLimitsForm = FormGroup;

/** Grupo de límites; el intervalo mínimo se captura en minutos y se envía en segundos. */
export function buildMitLimitsForm(fb: FormBuilder, limits?: MitLimits | null): MitLimitsForm {
  const amount = [Validators.min(0.01)];
  const count = [Validators.min(1)];
  return fb.group({
    maxAmountPerTransaction: [limits?.maxAmountPerTransaction ?? null, [Validators.required, ...amount]],
    maxAmountPerMonth: [limits?.maxAmountPerMonth ?? null, [Validators.required, ...amount]],
    minAmountPerTransaction: [limits?.minAmountPerTransaction ?? null, amount],
    maxAmountPerDay: [limits?.maxAmountPerDay ?? null, amount],
    maxTotalAmount: [limits?.maxTotalAmount ?? null, amount],
    maxTransactionsPerDay: [limits?.maxTransactionsPerDay ?? null, count],
    maxTransactionsPerMonth: [limits?.maxTransactionsPerMonth ?? null, count],
    maxTotalTransactions: [limits?.maxTotalTransactions ?? null, count],
    minIntervalMinutes: [limits?.minIntervalSeconds != null ? Math.round(limits.minIntervalSeconds / 60) : null, count],
    maxConsecutiveDeclines: [limits?.maxConsecutiveDeclines ?? 3, count]
  }, { validators: mitLimitsCoherence });
}

function mitLimitsCoherence(group: AbstractControl): ValidationErrors | null {
  const v = group.value as Record<string, number | null>;
  const errors: ValidationErrors = {};
  const perTx = v['maxAmountPerTransaction'];
  if (perTx != null && v['maxAmountPerMonth'] != null && perTx > v['maxAmountPerMonth']) errors['perTxOverMonth'] = true;
  if (perTx != null && v['maxAmountPerDay'] != null && perTx > v['maxAmountPerDay']) errors['perTxOverDay'] = true;
  if (perTx != null && v['minAmountPerTransaction'] != null && v['minAmountPerTransaction'] > perTx) errors['minOverMax'] = true;
  if (v['maxAmountPerDay'] != null && v['maxAmountPerMonth'] != null && v['maxAmountPerDay'] > v['maxAmountPerMonth']) errors['dayOverMonth'] = true;
  if (perTx != null && v['maxTotalAmount'] != null && v['maxTotalAmount'] < perTx) errors['totalUnderPerTx'] = true;
  return Object.keys(errors).length ? errors : null;
}

export function mitLimitsFromForm(form: MitLimitsForm): MitLimitsRequest {
  const v = form.getRawValue() as Record<string, number | null>;
  const minutes = v['minIntervalMinutes'];
  return {
    maxAmountPerTransaction: v['maxAmountPerTransaction']!,
    maxAmountPerMonth: v['maxAmountPerMonth']!,
    minAmountPerTransaction: v['minAmountPerTransaction'] ?? undefined,
    maxAmountPerDay: v['maxAmountPerDay'] ?? undefined,
    maxTotalAmount: v['maxTotalAmount'] ?? undefined,
    maxTransactionsPerDay: v['maxTransactionsPerDay'] ?? undefined,
    maxTransactionsPerMonth: v['maxTransactionsPerMonth'] ?? undefined,
    maxTotalTransactions: v['maxTotalTransactions'] ?? undefined,
    minIntervalSeconds: minutes != null ? minutes * 60 : undefined,
    maxConsecutiveDeclines: v['maxConsecutiveDeclines'] ?? undefined
  };
}

export const MIT_LIMITS_ERROR_TEXT: Record<string, string> = {
  perTxOverMonth: 'El máximo por cobro no puede ser mayor al máximo mensual.',
  perTxOverDay: 'El máximo por cobro no puede ser mayor al máximo diario.',
  minOverMax: 'El mínimo por cobro no puede ser mayor al máximo por cobro.',
  dayOverMonth: 'El máximo diario no puede ser mayor al máximo mensual.',
  totalUnderPerTx: 'El máximo acumulado no puede ser menor al máximo por cobro.'
};

@Component({
  selector: 'app-mit-limits-fields',
  templateUrl: './mit-limits-fields.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ReactiveFormsModule, FloatLabelModule, InputNumberModule]
})
export class MitLimitsFieldsComponent {
  form = input.required<MitLimitsForm>();
  idPrefix = input('mit-limits');

  readonly errorText = MIT_LIMITS_ERROR_TEXT;

  groupErrors(): string[] {
    const errors = this.form().errors;
    return errors ? Object.keys(errors).map((key) => this.errorText[key] ?? key) : [];
  }

  invalid(name: string): boolean {
    const control = this.form().get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }
}
